import { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  IconButton,
} from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Read-only display of a story's delivery_spec (#766 Phase 2/3).
 * The delivery spec is generated at the outline stage and drives script
 * generation: an overall intent, the rolled narrative form, concrete
 * specifics to preserve, the verified facts inventory (also client-facing via
 * stories_public), and per-beat structure (share of the episode, payoff,
 * feel, host state). Shown under the outline since they are the two artifacts
 * of the outline step. Tolerant of snake_case OR camelCase keys (jsonb may
 * arrive either way depending on key conversion), and of legacy specs whose
 * beats carry absolute `words` instead of `share` (both are weights; the
 * script stage normalizes against the actual sum).
 */

interface RawBeat {
  n?: number;
  summary?: string;
  share?: number;
  words?: number; // legacy absolute budget (pre-share specs)
  payoff?: 'minor' | 'major' | null;
  feel?: string;
  host_state?: string;
  hostState?: string;
}

interface RawFact {
  fact?: string;
  confidence?: 'settled' | 'reported' | 'contested';
}

interface RawSpec {
  intent?: string;
  form?: string;
  specifics?: string[];
  facts?: RawFact[];
  beats?: RawBeat[];
}

// Same emoji-bullet convention as the client facts tab.
const factConfidence: Record<string, { emoji: string; label: string }> = {
  settled: { emoji: '✅', label: 'Well-established' },
  reported: { emoji: '🔹', label: 'Reported, but not fully confirmed' },
  contested: { emoji: '🔸', label: 'Contested — sources disagree' },
};

const payoffColor: Record<string, 'default' | 'info' | 'success'> = {
  major: 'success',
  minor: 'info',
};

export function DeliverySpecDisplay({
  spec,
  formKey,
}: {
  spec: RawSpec | null | undefined;
  formKey?: string | null;
}) {
  const [open, setOpen] = useState(false);

  if (!spec || !Array.isArray(spec.beats) || spec.beats.length === 0) {
    return (
      <Box sx={{ mt: 1, mb: 1 }}>
        <Typography variant="body2" color="text.disabled">
          Delivery spec: none (generated with the outline)
        </Typography>
      </Box>
    );
  }

  const facts = Array.isArray(spec.facts) ? spec.facts : [];

  // Shares are weights (≈ percent); legacy specs carried absolute words.
  const isLegacyWords = spec.beats.some(b => b.share == null && b.words != null);
  const beatWeight = (b: RawBeat) => b.share ?? b.words ?? 0;
  const totalWeight = spec.beats.reduce((sum, b) => sum + beatWeight(b), 0);
  const budgetCell = (b: RawBeat): string => {
    if (isLegacyWords) return String(beatWeight(b));
    if (totalWeight <= 0) return '—';
    return `${Math.round((beatWeight(b) / totalWeight) * 100)}%`;
  };

  return (
    <Box sx={{ mt: 1, mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        <IconButton size="small" sx={{ p: 0.25 }}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </IconButton>
        <Typography variant="subtitle2">Delivery spec</Typography>
        {(spec.form || formKey) && spec.form !== 'none' && (
          <Chip size="small" label={`form: ${spec.form || formKey}`} variant="outlined" />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {spec.beats.length} beats
          {facts.length > 0 ? ` · ${facts.length} facts` : ''}
          {isLegacyWords ? ` · ~${totalWeight} words (legacy)` : ''}
        </Typography>
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          {spec.intent && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Intent:</strong> {spec.intent}
            </Typography>
          )}
          {Array.isArray(spec.specifics) && spec.specifics.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Specifics:</strong> {spec.specifics.join(' · ')}
            </Typography>
          )}
          {facts.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>Facts</strong>
              </Typography>
              {facts.map(f => {
                const conf = f.confidence ? factConfidence[f.confidence] : undefined;
                return (
                  <Typography
                    key={f.fact}
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.25 }}
                  >
                    {conf ? `${conf.emoji} ` : '• '}
                    {f.fact}
                  </Typography>
                );
              })}
              <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                {Object.entries(factConfidence)
                  .filter(([key]) => facts.some(f => f.confidence === key))
                  .map(([, { emoji, label }]) => `${emoji} ${label}`)
                  .join(' · ')}
              </Typography>
            </Box>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 24 }}>#</TableCell>
                <TableCell>Beat</TableCell>
                <TableCell sx={{ width: 60 }}>
                  {isLegacyWords ? 'Words' : 'Share'}
                </TableCell>
                <TableCell sx={{ width: 70 }}>Payoff</TableCell>
                <TableCell sx={{ width: 120 }}>Feel</TableCell>
                <TableCell sx={{ width: 160 }}>Hosts</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {spec.beats.map((b, i) => (
                <TableRow key={b.n ?? i}>
                  <TableCell>{b.n ?? i + 1}</TableCell>
                  <TableCell>{b.summary}</TableCell>
                  <TableCell>{budgetCell(b)}</TableCell>
                  <TableCell>
                    {b.payoff ? (
                      <Chip size="small" label={b.payoff} color={payoffColor[b.payoff] || 'default'} />
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{b.feel}</TableCell>
                  <TableCell>{b.host_state ?? b.hostState}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Collapse>
    </Box>
  );
}
